import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AbstractControl, FormControl, FormGroup, ValidatorFn } from '@angular/forms';
import moment from 'moment';

@Injectable(
    { providedIn: 'root' }
)
export class UtilsProvider {
    public EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
    public SWISS_PHONE_NUMBER_REGEX = /^(0041|041|\+41|\+\+41|41)?(0|\(0\))?([1-9]\d{1})(\d{3})(\d{2})(\d{2})$/;

    public FLOAT_VALIDATOR =  (): ValidatorFn => (control: AbstractControl): {[key: string]: any} | null => {
        if (control.value === undefined || control.value === null) {
            return null;
        }
        const value = '' + control.value;
        if (value.length === 0) {
            return null;
        }

        const match = value.match(/^[+-]?([0-9]+([.][0-9]*)?|[.][0-9]+)$/g);
        const error = { floatValue: {value: control.value}};
        return match ? null : error;
    };
    constructor(
        private _translater: TranslateService,
    ) {

    }

    public removeDuplicates(arr: any[]): any[] {
        const uniqueArr = [];
        for (let i = 0; i < arr.length; i++) {
            if (uniqueArr.indexOf(arr[i]) < 0) {
                uniqueArr.push(arr[i]);
            }
        }
        return uniqueArr;
    }

    public findIn(key: any, items: any[], keyName = 'id'): any {
        for (let i = 0; i < items.length; i++) {
            if (items[i][keyName] === key) {
                return items[i];
            }
        }
        return null;
    }

    public findManyLike(keyName: string, value: string, items: any[]){
        const match: any[] = [];
        for (let i = 0; i < items.length; i++) {
            const str = items[i][keyName].toString();
            if (str.indexOf(value) > -1) {
                match.push(items[i]);
            }
        }
        return match;
    }

    public findManyIn(keys: any[], items: any[], keyName = 'id'): any[] {
        const match: any[] = [];
        for (let i = 0; i < items.length; i++) {
            let found = false;
            for (let j = 0; j < keys.length && !found; j++) {
                if (items[i][keyName] == keys[j]) {
                    found = true;
                    match.push(items[i]);
                }
            }
        }

        return match;
    }

    /**
     * Recherche plusieurs résultats dans un sous-objet.
     * Si le tableau dans lequel on cherche une valeur contient des éléments composés de tableaux,
     * on peut rechercher une valeur spécifique dans un des tableaux de cette élément. Dans l'exemple,
     * je recherche dans le tableau des adresses, toutes les adresses appartenant à un athlète.
     * Voir l'interface "AddressRow" du fichier app\addresses\provider address-provider.ts.
     * Exemple : let addresses = this.findManyInSubObject([athlete.id], addresses, 'address', 'athleteId')
     * @param keys
     * @param items
     * @param subObject
     * @param keyName
     */
    public findManyInSubObject(keys: any[], items: any[], subObject: string, keyName = 'id'): any[] {
        const match: any[] = [];
        for (let i = 0; i < items.length; i++) {
            let found = false;
            for (let j = 0; j < keys.length && !found; j++) {
                if (items[i][subObject][keyName] == keys[j]) {
                    found = true;
                    match.push(items[i]);
                }
            }
        }

        return match;
    }

    public normalizeString(str: string): string {
        // inspiré de : https://stackoverflow.com/a/37511463
        return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
    }

    public getKeyValues(items: any[], keyName = 'id', removeDuplicate = true, filterNull = true): any[] {
        let values = [];
        for (let i = 0; i < items.length; i++) {
            if (items[i] && (!filterNull || items[i][keyName])) {
                values.push(items[i][keyName]);
            }
        }
        if (removeDuplicate) {
            values = this.removeDuplicates(values);
        }
        return values;
    }

    public hasOneOfs(items: any[], searchItems: any[]): boolean {
        for (let i = 0; i < items.length; i++) {
            for (let j = 0; j < searchItems.length; j++) {
                if (items[i] === searchItems[j]) {
                    return true;
                }
            }
        }

        return false;
    }

    public getIndexOfObject(items: any[], objectKeyName: string, keyValue: any): number {

        // empty array
        if (items.length === 0){
            return -1;
        }

        for (let i = 0; i < items.length; i++) {
            if (items[i][objectKeyName] && items[i][objectKeyName] === keyValue){
                return i;
            }
        }

        // not found
        return -1;
    }

    public sortRow(a: string | number, b: string | number, nullAfter = true): number {
        if (a) {
            if (b) {
                if (a < b) { return -1; }
                if (a > b) { return 1; }
                return 0;
            }

            return -1;
        }

        if (b) {
            return 1;
        }

        return 0;
    }

    public nowISO(): string {
        return (new Date()).toISOString();
        // toISOString convertit tout seul la date en UTC :o)
        /*
        let now = new Date();
        now.setMinutes(now.getMinutes() + now.getTimezoneOffset());
        return now.toISOString();
        */
    }

    public secondsToFormattedDuration(seconds: number, format = 'default'): string {

        let toReturn = '';
        const toFormat = seconds;

        if (format === 'default' && toFormat >= (60 * 100)) {
            format = 'hh:mm:ss';
        }

        switch (format) {

        case 'hh:mm:ss':
            toReturn = this.secondsToFormattedHourMinSec(toFormat);
            break;
        case 'default':
        case 'mm:ss':
        default:
            toReturn = this.secondsToFormattedMinSec(toFormat);
            break;

        }

        return toReturn;

    }

    public formattedDurationToSeconds(formatted: any, defaultValue = 0): number {

        let toReturn: number = defaultValue;

        switch (formatted.toString().split(':').length) {

        case 2:
            toReturn = this.formattedMinSecToSeconds(formatted, defaultValue);
            break;

        case 3:
            toReturn = this.formattedHourMinSecToSeconds(formatted, defaultValue);
            break;

        default:
            toReturn = defaultValue;
            break;

        }

        return isNaN(toReturn) ? defaultValue : toReturn;

    }

    public formattedMinSecToSeconds(formatted: string, defaultValue = 0) {

        let toReturn: number = defaultValue;

        const min = parseInt(formatted.split(':')[0]);
        const sec = parseInt(formatted.split(':')[1]);

        toReturn = (min * 60) + sec;

        return toReturn;

    }

    public formattedHourMinSecToSeconds(formatted: string, defaultValue = 0) {

        let toReturn: number = defaultValue;

        const hour = parseInt(formatted.split(':')[0]);
        const min = parseInt(formatted.split(':')[1]);
        const sec = parseInt(formatted.split(':')[2]);

        toReturn = (hour * 3600) + (min * 60) + sec;

        return toReturn;

    }

    public secondsToFormattedHourMinSec(seconds: number, defaultValue = '00:00:00'): string {

        let hour = String(Math.floor(seconds / 3600));
        seconds %= 3600;
        let min = String(Math.floor(seconds / 60));
        let sec = String(seconds % 60);

        const padding = '00';

        sec = padding.substring(0, padding.length - sec.length) + sec;
        min = padding.substring(0, padding.length - min.length) + min;
        hour = padding.substring(0, padding.length - hour.length) + hour;

        return hour + ':' + min + ':' + sec;

    }

    public secondsToFormattedMinSec(seconds: number, defaultValue = '00:00'): string {

        if (seconds >= (60 * 100)) {
            return defaultValue;
        }

        let sec = String(seconds % 60);
        let min = String(Math.floor(seconds / 60));
        const padding = '00';

        sec = padding.substring(0, padding.length - sec.length) + sec;
        min = padding.substring(0, padding.length - min.length) + min;

        return min + ':' + sec;

    }

    public nowTimestamp(): number {
        const now = new Date();
        const timestamp = Math.round(now.getTime() / 1000);
        const timezoneOffset = now.getTimezoneOffset() * 60;
        return timestamp - timezoneOffset;
    }

    public ISODateToYmdHis(date: string): string {
        return date.replace('T', ' ').substr(0, 19);
    }

    public DateToYmdHis(date: Date, adjustTimezone = true): string {
        if (adjustTimezone) {
            return this.ISODateToYmdHis(date.toISOString());
        }

        const newDate: Date = new Date(date.getTime() - (date.getTimezoneOffset() * 60 * 1000));
        return this.ISODateToYmdHis(newDate.toISOString());
    }

    public DateToYmd(date: Date): string {
        return date.getFullYear() + '-' +
            this.addTrailingChar((date.getMonth() + 1) + '', '0', 2) + '-' +
            this.addTrailingChar(date.getDate() + '', '0', 2);
    }

    public DateToHm(date: Date, separator = ':'): string {
        return this.addTrailingChar(date.getHours().toString(), '0', 2) + separator + this.addTrailingChar(date.getMinutes().toString(), '0', 2);
    }

    public momentToYmd(m: any): string {
        return m.format('YYYY-MM-DD');
    }

    public firstDayOfWeek(date: Date): Date {
        moment.updateLocale('fr', {
            week: {
                dow: 1,
            }
        });
        const dateMoment = moment(date);
        return this.dateAtMidnight(dateMoment.startOf('week').toDate());
    }

    public lastDayOfWeek(date: Date): Date {
        moment.updateLocale('fr', {
            week: {
                dow: 1,
            }
        });
        const dateMoment = moment(date);
        return this.dateAtMidnight(dateMoment.endOf('week').toDate());
    }

    public YmdHisToDate(date: string): Date | null {
        // on reçoit la date au format YYYY-mm-dd HH:ii:ss (iso8601)
        // mais safari n'arrive pas à parser correctement ce format...
        // let date: Date = new Date(dateStr);
        const parts: string[] = date.split(' ');
        if (parts.length !== 2) {
            return null;
        }
        const dateParts: string[] = parts[0].split('-');
        const timeParts: string[] = parts[1].split(':');
        if (dateParts.length !== 3 || timeParts.length !== 3) {
            return null;
        }
        return new Date(
            parseInt(dateParts[0], 10),
            parseInt(dateParts[1], 10) - 1,
            parseInt(dateParts[2], 10),
            parseInt(timeParts[0], 10),
            parseInt(timeParts[1], 10),
            parseInt(timeParts[2], 10),
            0
        );
    }

    public YmdToDate(date: string): Date | null {
        const dateParts: string[] = date.split('-');
        if (dateParts.length !== 3) {
            return null;
        }
        return new Date(
            parseInt(dateParts[0], 10),
            parseInt(dateParts[1], 10) - 1,
            parseInt(dateParts[2], 10)
        );
    }

    public dateAtMidnight(date: Date): Date {
        date.setHours(0);
        date.setMinutes(0);
        date.setSeconds(0);
        date.setMilliseconds(0);

        return date;
    }

    public getWeekNumber(date: Date, locale?: string): number {
        const dateMoment = moment(date);

        if (locale) {
            dateMoment.locale(locale);
        }

        return parseInt(dateMoment.format('w'), 10);
    }

    public addTrailingChar(str: string, char: string, targetStrLength: number): string {
        const toAddCount: number = targetStrLength - str.length;
        for (let i = 0; i < toAddCount; i++) {
            str = char + str;
        }

        return str;
    }

    public formatDate(dateStr: string, format: string, adjustTimezone = true): string {
        // TODO finir ca ...

        const oneDay: number = 1000 * 60 * 60 * 24;
        const months: string[] = [
            'January', 'February', 'March',
            'April', 'May', 'June',
            'July', 'August', 'September',
            'October', 'November', 'December'
        ];
        const days: string[] = [
            'Sunday', 'Monday', 'Tuesday', 'Wednesday',
            'Thursday', 'Friday', 'Saturday'
        ];

        if (!dateStr || dateStr.length === 0) {
            return '';
        }

        const date: Date | null = this.YmdHisToDate(dateStr);
        if (!date) {
            return '';
        }
        let dateTimestamp: number = date.getTime();
        if (adjustTimezone) {
            dateTimestamp = date.getTime() - (date.getTimezoneOffset() * 60 * 1000);
            date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
        }

        if (format === 'closest' || format === 'closest-short') {
            // examples (closest) :
            //  - DateFormat_FjY:           December 23, 2021
            //  - DateFormat_NextlG:        Next Monday, at 7:33
            //  - DateFormat_TomorrowG:     Tomorrow, at 10:51
            //  - DateFormat_TodayG:        Today, at 18:23
            //  - DateFormat_YesterdayG:    Yesterday, at 9:21
            //  - DateFormat_LastlG:        Last Tuesday, at 15:05
            //  - DateFormat_FjY:           August 4, 2017
            // examples (closest-short) :
            //  - DateFormat_mdY:               12.23.2017
            //  - DateFormat_Tomorrow:          Tomorrow
            //  - DateFormat_Hi:                18:23
            //  - DateFormat_Yesterday:         Yesterday
            //  - DateFormat_mdY:               08.04.2017

            const yesterday: Date = new Date();
            yesterday.setHours(0);
            yesterday.setMinutes(0);
            yesterday.setSeconds(0);
            yesterday.setMilliseconds(0);
            const tomorrow: Date = new Date();
            tomorrow.setHours(23);
            tomorrow.setMinutes(59);
            tomorrow.setSeconds(59);
            tomorrow.setMilliseconds(999);
            const yesterdayTimestamp: number = yesterday.getTime();
            const tomorrowTimestamp: number = tomorrow.getTime();

            if (dateTimestamp < yesterdayTimestamp) {
                // on va chercher dans le passé ...
                if (dateTimestamp >= (yesterdayTimestamp - oneDay)) {
                    if (format === 'closest') {
                        return this._translater.instant('DateFormat_YesterdayG', {
                            hours: date.getHours(),
                            minutes: this.addTrailingChar('' + date.getMinutes(), '0', 2)
                        });
                    } else if (format === 'closest-short') {
                        return this._translater.instant('DateFormat_Yesterday');
                    }
                } else if (dateTimestamp >= (yesterdayTimestamp - (6 * oneDay))) {
                    if (format === 'closest') {
                        return this._translater.instant('DateFormat_LastlG', {
                            weekDay: this._translater.instant(days[date.getDay()]),
                            hours: date.getHours(),
                            minutes: this.addTrailingChar('' + date.getMinutes(), '0', 2)
                        });
                    } else if (format === 'closest-short') {
                        return this._translater.instant('DateFormat_mdY', {
                            year: date.getFullYear(),
                            month: this.addTrailingChar('' + (date.getMonth() + 1), '0', 2),
                            day: this.addTrailingChar('' + (date.getDate() + 1), '0', 2)
                        });
                    }
                }
            } else if (dateTimestamp > tomorrowTimestamp) {
                // on va chercher dans le futur ...
                if (dateTimestamp <= (tomorrowTimestamp + oneDay)) {
                    if (format === 'closest') {
                        return this._translater.instant('DateFormat_TomorrowG', {
                            hours: date.getHours(),
                            minutes: this.addTrailingChar('' + date.getMinutes(), '0', 2)
                        });
                    } else if (format === 'closest-short') {
                        return this._translater.instant('DateFormat_Tomorrow');
                    }
                } else if (dateTimestamp <= (tomorrowTimestamp + (6 * oneDay))) {
                    if (format === 'closest') {
                        return this._translater.instant('DateFormat_NextlG', {
                            weekDay: this._translater.instant(days[date.getDay()]),
                            hours: date.getHours(),
                            minutes: this.addTrailingChar('' + date.getMinutes(), '0', 2)
                        });
                    } else if (format === 'closest-short') {
                        return this._translater.instant('DateFormat_mdY', {
                            year: date.getFullYear(),
                            month: this.addTrailingChar('' + (date.getMonth() + 1), '0', 2),
                            day: this.addTrailingChar('' + (date.getDate() + 1), '0', 2)
                        });
                    }
                }
            } else {
                // c'est aujourd'hui ...
                if (format === 'closest') {
                    return this._translater.instant('DateFormat_TodayG', {
                        hours: date.getHours(),
                        minutes: this.addTrailingChar('' + date.getMinutes(), '0', 2)
                    });
                } else if (format === 'closest-short') {
                    return this._translater.instant('DateFormat_Hi', {
                        hours: this.addTrailingChar('' + date.getHours(), '0', 2),
                        minutes: this.addTrailingChar('' + date.getMinutes(), '0', 2)
                    });
                }
            }

            if (format === 'closest') {
                return this._translater.instant('DateFormat_FjY', {
                    year: date.getFullYear(),
                    month: this._translater.instant(months[date.getMonth()]),
                    day: date.getDate()
                });
            } else if (format === 'closest-short') {
                return this._translater.instant('DateFormat_mdY', {
                    year: date.getFullYear(),
                    month: this.addTrailingChar('' + (date.getMonth() + 1), '0', 2),
                    day: this.addTrailingChar('' + (date.getDate() + 1), '0', 2)
                });
            }
        } else if (format === 'full-date') {
            return this._translater.instant('DateFormat_lFdY', {
                weekDay: this._translater.instant(days[date.getDay()]),
                day: date.getDate(),
                month: this._translater.instant(months[date.getMonth()]),
                year: date.getFullYear()
            });
        } else if (format === 'FjY') {
            return this._translater.instant('DateFormat_FjY', {
                year: date.getFullYear(),
                month: this._translater.instant(months[date.getMonth()]),
                day: date.getDate()
            });
        } else if (format === 'mdY') {
            return this._translater.instant('DateFormat_mdY', {
                year: date.getFullYear(),
                month: this.addTrailingChar('' + (date.getMonth() + 1), '0', 2),
                day: this.addTrailingChar('' + date.getDate(), '0', 2)
            });
        } else if (format === 'LLLd') {
            return this._translater.instant('DateFormat_LLLd', {
                month: this._translater.instant(months[date.getMonth()]).substr(0, 3) + '.',
                day: date.getDate()
            });
        }

        return dateStr;
    }

    public formatNumber(num: number, decimalsCount: number): string {
        let formated = num.toFixed(decimalsCount);
        const parts = ('' + formated).split('.');
        formated = parts[0].replace(/(.)(?=(\d{3})+$)/g, '$1\'');
        if (parts.length > 1) { formated += '.' + parts[1]; }
        return formated;
    }

    public randomString(length: number, buffer = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'): string {
        let str = '';
        for (let i = 0; i < length; i++) {
            str += buffer.charAt(Math.floor(Math.random() * buffer.length));
        }

        return str;
    }

    public convertToHours(minutes: number): string {
        const hours: number = parseInt('' + (minutes / 60));
        const remainingMinutes: number = minutes % 60;

        return this._translater.instant(hours > 1 ? 'x_hours' : 'x_hour', { x: hours }) +
            ' ' +
            this._translater.instant(remainingMinutes > 1 ? 'x_minutes' : 'x_minute', { x: remainingMinutes });
    }

    /**
     * Return an object { isValid: true } if validation succeeds. Return an object { isValid: false, code: 745, message: <the error message ready to be displayed>} otherwise.
     * Validations supported (examples) :
     * - type[string]:      'nullable|noTrim|min:5|max:5|in:salut,hello,guten tag,hola'
     * - type[int]:         'nullable|min:-3|max:1200|in:3,4,500'
     * - type[decimal]:     'nullable|min:-2.1|max:22.0|in:1.0,1.1,1.2'
     * - type[url]:         'nullable'
     * @param data
     * @param type
     * @param rulesString
     * @param fieldName
     */
    public validateFormField(data: any, type: string, rulesString: string, fieldName: string): any {
        // TODO remplacer une bonne partie de ce cheni par validator.js : https://github.com/chriso/validator.js
        // le jour où je saurais comment en faire un module angular ...

        const validReturn = { isValid: true };

        const error: any = (messageKey: string, params: any[]): any => {
            const message: string = this._translater.instant(messageKey, params);
            return { isValid: false, code: 745, message };
        };

        const has: any = (key: string, rules: string[]): boolean => {
            for (let i = 0; i < rules.length; i++) {
                if (rules[i].split(':')[0] === key) {
                    return true;
                }
            }
            return false;
        };

        const rules: string[] = rulesString.split('|');

        const canBeNull: boolean = has('nullable', rules);
        if (canBeNull && (data === null || data === undefined)) {
            return validReturn;
        }

        //
        // STRING
        //
        if (type === 'string') {

            if (data && !has('noTrim', rules)) {
                data = data.trim();
            }

            for (let i = 0; i < rules.length; i++) {
                const rule: string[] = rules[i].split(':');
                if (rule[0] === 'min') {
                    if (!data || data.length < parseInt(rule[1], 10)) {
                        return error('The_field_x_must_be_y_characters_at_min', { fieldName, min: rule[1] });
                    }
                } else if (rule[0] === 'max') {
                    if (!data || data.length > parseInt(rule[1], 10)) {
                        return error('The_field_x_must_be_y_characters_at_max', { fieldName, max: rule[1] });
                    }
                } else if (rule[0] === 'in') {
                    const items: string[] = rules[1].split(',');
                    let isIn = false;
                    for (let j = 0; j < items.length && !isIn; j++) {
                        isIn = items[j] === data;
                    }
                    if (!isIn) {
                        return error('The_field_x_must_be_one_of_y', { fieldName, items: items.join(', ') });
                    }
                }
            }
        } else if (type === 'integer') {
            const int: number = parseInt(data, 10);
            if (isNaN(int)) {
                return error('The_field_x_must_be_an_integer', { fieldName });
            }

            for (let i = 0; i < rules.length; i++) {
                const rule: string[] = rules[i].split(':');
                if (rule[0] === 'min') {
                    if (int < parseInt(rule[1], 10)) {
                        return error('The_field_x_must_be_greater_than_y', { fieldName, min: rule[1] });
                    }
                } else if (rule[0] === 'max') {
                    if (int > parseInt(rule[1], 10)) {
                        return error('The_field_x_must_be_lower_than_y', { fieldName, max: rule[1] });
                    }
                } else if (rule[0] === 'in') {
                    const items: string[] = rules[1].split(',');
                    let isIn = false;
                    for (let j = 0; j < items.length && !isIn; j++) {
                        isIn = parseInt(items[j], 10) === int;
                    }
                    if (!isIn) {
                        return error('The_field_x_must_be_one_of_y', { fieldName, items: items.join(', ') });
                    }
                }
            }
        } else if (type === 'decimal') {
            const float: number = parseFloat(data);
            if (isNaN(float)) {
                return error('The_field_x_must_be_a_decimal', { fieldName });
            }

            for (let i = 0; i < rules.length; i++) {
                const rule: string[] = rules[i].split(':');
                if (rule[0] === 'min') {
                    if (float < parseInt(rule[1], 10)) {
                        return error('The_field_x_must_be_greater_than_y', { fieldName, min: rule[1] });
                    }
                } else if (rule[0] === 'max') {
                    if (float > parseInt(rule[1], 10)) {
                        return error('The_field_x_must_be_lower_than_y', { fieldName, max: rule[1] });
                    }
                } else if (rule[0] === 'in') {
                    const items: string[] = rules[1].split(',');
                    let isIn = false;
                    for (let j = 0; j < items.length && !isIn; j++) {
                        isIn = parseFloat(items[j]) === float;
                    }
                    if (!isIn) {
                        return error('The_field_x_must_be_one_of_y', { fieldName, items: items.join(', ') });
                    }
                }
            }
        } else if (type === 'url') {
            // http://stackoverflow.com/questions/3809401/what-is-a-good-regular-expression-to-match-a-url
            // surement pas la meilleure regex du monde pour ca, mais ca va pour nos besoins
            const expression = '[-a-zA-Z0-9@:%_\+.~#?&//=]{2,256}\.[a-z]{2,4}\b(\/[-a-zA-Z0-9@:%_\+.~#?&//=]*)?';
            const regex = new RegExp(expression, 'gi');
            const match: string[] = data.match(regex);
            if (match.length !== 1 || match[0] !== data) {
                return error('The_field_x_must_be_a_valid_url', { fieldName });
            }
        }

        return validReturn;
    }

    public buildTranslationParam(translations: any[]): any {
        const param: any = {};
        for (let i = 0; i < translations.length; i++) {
            const item: any = {};
            for (const key in translations[i].translation) {
                if (translations[i].translation.hasOwnProperty(key)) {
                    item[key] = translations[i].translation[key];
                }
            }
            param[translations[i].lang.code] = item;
        }

        return param;
    }

    public truncateText(text: string, maxChars: number, options: any = {}): string {
        if (maxChars < 5) {
            maxChars = 5;
        }
        if (options.ellipsize === undefined) {
            options.ellipsize = false;
        }
        if (options.ellipsisPosition === undefined) {
            options.ellipsisPosition = 'end';
        }

        if (text.length <= maxChars) {
            return text;
        }

        if (options.ellipsize) {
            if (options.ellipsisPosition === 'center') {
                const maxCharsHalf: number = parseInt('' + (maxChars / 2), 10);
                return text.substr(0, maxCharsHalf - 2) + ' ... ' + text.substr(text.length - maxCharsHalf + 3);
            } else {
                return text.substr(0, maxChars - 3) + '...';
            }
        }

        return text.substr(0, maxChars);
    }

    public buildModelParams(models: any[], langIds: number[]): any[] {
        /**
        * Construit un tableau d'objets ayant la structure suivante :
        *   [{
        *       item: { key: value, ... }
        *       translations: [
        *           { langId: number, key: value, ... },
        *       ]
        *   },]
        * Les valeurs traductibles du model sont recopiées dans toutes les langues activées
        */

        const items: any[] = [];
        for (let i = 0; i < models.length; i++) {
            const nonTranslatableValues: any = {};
            const nonTranslatableKeys: string[] = models[i].constructor.getNonTranslatableFields();
            for (let j = 0; j < nonTranslatableKeys.length; j++) {
                nonTranslatableValues[nonTranslatableKeys[j]] = models[i][nonTranslatableKeys[j]] === undefined ? null : models[i][nonTranslatableKeys[j]];
            }

            const translations: Object[] = [];
            const translatableKeys: string[] = models[i].constructor.getTranslatableFields();
            for (let j = 0; j < langIds.length; j++) {
                const translation: any = { langId: langIds[j] };
                for (let k = 0; k < translatableKeys.length; k++) {
                    translation[translatableKeys[k]] = models[i][translatableKeys[k]] === undefined ? null : models[i][translatableKeys[k]];
                }

                translations.push(translation);
            }

            items.push({
                item: nonTranslatableValues,
                translations
            });
        }

        return items;
    }

    public buildExerciceParams(models: any[], langIds: number[], ignore_video: boolean): any[] {
        /**
         * Construit un tableau de paramètres à partir des modèles fournis.
         *
         * Chaque entrée renvoyée a la forme :
         * {
         *   item:         { <champ non traduisible>: valeur, ... },
         *   translations: { <champ traduisible>   : valeur, ... },
         *   langs:        number[],
         *   ignore_video: boolean
         * }
         *
         * Règles :
         * - Les champs traduisibles sont copiés dans toutes les langues de `langIds` qui ne possèdent pas encore de traduction.
         * - Si une traduction existe pour une langue absente de `langIds`, elle est supprimée.
         *
         */

        const modelParams: any[] = [];

        models.forEach(model => {
            const fields: string[] = model.constructor.getNonTranslatableFields();
            const fieldsData: Record<string, any> = {};

            fields.forEach(field => {
                fieldsData[field] = model[field] !== undefined ? model[field] : null;
            });
    
            const translationFields: string[] = model.constructor.getTranslatableFields();
            const translationsData: Record<string, any> = {};

            translationFields.forEach(field => {
                translationsData[field] = model[field] !== undefined ? model[field] : null;
            });

            modelParams.push({
                item: fieldsData,
                translations: translationsData,
                langs: langIds,
                ignore_video: ignore_video
            });
        });
    
        return modelParams;    
    }

    public uuidv4() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0; const v = c == 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    public async dataURItoFile(dataURI: string | URL | Request): Promise<File> {
      const res = await fetch(dataURI);
      const blob = await res.blob();
      return (new File([blob], this.uuidv4()));
    }

    /**
     * Scroll to the first field having an error into the form
     */
    public scrollToFormInputError() {
        const firstElementWithError = document.querySelector('form .ng-invalid');
        if (firstElementWithError) {
            firstElementWithError.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
    }

    /**
     * Validate all form fields of a form group
     *
     * <code>
     * this.formGroup = this.fb.group({
     *     foo: new FormControl('', [Validators.required]),
     *     bar: new FormControl('', [Validators.required]),
     * });
     *
     * this.validateAllFormFields(this.formGroup);
     * </code>
     *
     * @param formGroup
     */
    public validateAllFormFields(formGroup: FormGroup) {
        Object.keys(formGroup.controls).forEach((field) => {
            const control = formGroup.get(field);
            if (control instanceof FormControl) {
                control.markAsTouched({ onlySelf: true });
            } else if (control instanceof FormGroup) {
                this.validateAllFormFields(control);
            }
        });
    }

    /**
     * Remove the protocole of an Url
     * @param url
     */
    public clearUrlProtocole(url: string){
        return url.replace(/(^\w+:|^)\/\//, '');
    }

    /**
     * Retourne un tag unique
     * @return String
     */
    public getUniqTag() {
        return Math.random().toString(36).substr(2, 9) + Math.random().toString(36).substr(2, 9) + Math.random().toString(36).substr(2, 9);
    }

    /**
     * Switch 2 items in an array
     * @param items
     * @param fromIndex
     * @param toIndex
     */
    public switchArrayItems(items: any[], fromIndex: number, toIndex: number){

        if (fromIndex < 0 || fromIndex > (items.length - 1)){
            return false;
        }
        if (toIndex < 0 || toIndex > (items.length - 1)){
            return false;
        }

        const toMove = items[toIndex];
        items[toIndex] = items[fromIndex];
        items[fromIndex] = toMove;

        return items;
    }
}

export function importToArray<Key extends string, PropType>(importObject: any): PropType[] {
    const keys = Object.getOwnPropertyNames(importObject);
    return keys.filter((key) => key.indexOf('__') !== 0).map((key) => importObject[key]);
}

export function castToModel<T>(data: any, model: new (...args: any[]) => T ): T {
    const classInstance: any = new model();
    const classProps = Object.getOwnPropertyNames(classInstance);

    classProps.forEach((prop) => {
        classInstance[prop] = data[prop];
    });

    return classInstance;
}
